import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Lightweight pagination bar.
 * Props:
 *   page         - current page number (1-based)
 *   pageCount    - total pages
 *   total        - total item count (for label)
 *   pageSize     - items per page
 *   onPageChange - (nextPage: number) => void
 *   idPrefix     - data-testid prefix
 */
export function Pagination({ page, pageCount, total, pageSize, onPageChange, idPrefix = "pg" }) {
  if (pageCount <= 1) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  // Compact window: always show 1, page-1, page, page+1, last, with ellipses.
  const pages = [];
  const push = (p) => { if (!pages.includes(p) && p >= 1 && p <= pageCount) pages.push(p); };
  push(1);
  push(page - 1); push(page); push(page + 1);
  push(pageCount);
  pages.sort((a, b) => a - b);
  // Insert ellipsis markers (null) where there are gaps
  const withGaps = [];
  pages.forEach((p, i) => {
    if (i > 0 && p - pages[i - 1] > 1) withGaps.push("…");
    withGaps.push(p);
  });

  return (
    <div className="flex items-center justify-between gap-3 px-1 py-2 text-xs text-gray-600" data-testid={`${idPrefix}-pagination`}>
      <span>
        Showing <span className="font-medium text-[#1A2744]">{from}</span>–
        <span className="font-medium text-[#1A2744]">{to}</span> of{" "}
        <span className="font-medium text-[#1A2744]">{total}</span>
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="p-1.5 rounded-md border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`${idPrefix}-prev`}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        {withGaps.map((p, idx) =>
          p === "…" ? (
            <span key={`g-${idx}`} className="px-2 text-gray-400">…</span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              className={[
                "min-w-[28px] px-2 py-1 rounded-md text-xs font-medium border",
                p === page
                  ? "bg-[#1A2744] text-white border-[#1A2744]"
                  : "text-gray-700 border-gray-200 hover:bg-gray-50",
              ].join(" ")}
              data-testid={`${idPrefix}-page-${p}`}
            >
              {p}
            </button>
          )
        )}
        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= pageCount}
          className="p-1.5 rounded-md border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`${idPrefix}-next`}
          aria-label="Next page"
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

/**
 * Tiny hook that slices an array into a page, clamps page when the array
 * shrinks, and returns everything needed to render <Pagination>.
 */
import { useEffect, useMemo, useState } from "react";

export function usePagination(items, pageSize = 10) {
  const [page, setPage] = useState(1);
  const total = items?.length || 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => {
    if (page > pageCount) setPage(1);
  }, [pageCount, page]);
  const pageItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return (items || []).slice(start, start + pageSize);
  }, [items, page, pageSize]);
  return { page, setPage, pageCount, pageItems, total, pageSize };
}
